import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';

import { InitiativeFieldTypes } from '@/features/canvas/constants/initiative-field';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';

import { getInitiativeDates } from '../../utils/getInitiativeDates';

interface InitiativeBarProps {
  allInitiatives: FilteredNodes[];
  initiativeIndex: number;
  isDependencyMode: boolean;
  node: FilteredNodes;
  timelineEnd: Dayjs;
  timelineStart: Dayjs;
  // Tree depth for styling
  depth?: number;
  isConnectedToIsolated?: boolean;
  isIsolated?: boolean;
  isIsolationActive?: boolean;
  // Gantt-specific isolation props
  onBarClick?: (initiativeId: number) => void;
  // Position and height
  rowHeight?: number;
  topPosition?: number;
}

const InitiativeBar: React.FC<InitiativeBarProps> = ({
  allInitiatives,
  node,
  timelineStart,
  timelineEnd,
  initiativeIndex,
  isDependencyMode,
  onBarClick,
  isIsolated = false,
  isConnectedToIsolated = false,
  isIsolationActive = false,
  depth = 0,
  topPosition: providedTopPosition,
  rowHeight = 28,
}) => {
  const {
    setSelectedNodeId,
    setIsInitiativeDetailDialogOpen,
    activeFilterIds,
    optionCheckedIds,
    filters,
  } = useEditorContext();
  const theme = useTheme();

  // Apply exact same filtering logic as CanvasNodes (Sunray view)
  const activeFilters =
    filters?.filter(f => f.id !== undefined && activeFilterIds.includes(f.id!)) || [];

  const doesNodeHaveActiveFilter = activeFilters.some(
    filter => filter.nodes?.some(n => n.nodeId === node.id) ?? false
  );

  const isNodesOptionChecked = activeFilters.every(filter =>
    filter.selection === 'multiple'
      ? filter.nodes?.some(
          n => n.nodeId === node.id && optionCheckedIds.includes(n.optionId ?? -1)
        ) ?? false
      : optionCheckedIds.includes(filter.nodes?.find(n => n.nodeId === node.id)?.optionId ?? -1)
  );

  const isNotInFilter = Boolean(
    (doesNodeHaveActiveFilter && optionCheckedIds.length > 0 && !isNodesOptionChecked) ||
      (!doesNodeHaveActiveFilter && activeFilterIds.length > 0)
  );

  const isOrphan = useMemo(() => {
    if (!isDependencyMode) return false;
    const nodeId = node.id;
    const initiativeId = node.initiatives?.id;
    const isNodeHidden = (checkNode: FilteredNodes) => {
      const { startDate, endDate } = getInitiativeDates(checkNode.initiatives);
      return !startDate && !endDate;
    };
    const hasVisibleOutgoing =
      node.dependencies &&
      node.dependencies.length > 0 &&
      node.dependencies.some(dep => {
        const target = allInitiatives.find(
          n => n.id === dep.slaveNodeId || n.initiatives?.id === dep.slaveNodeId
        );
        return target && !isNodeHidden(target);
      });
    const hasVisibleIncoming = allInitiatives.some(other => {
      if (isNodeHidden(other)) return false;
      return other.dependencies?.some(
        dep =>
          dep.masterNodeId === nodeId ||
          dep.masterNodeId === initiativeId ||
          dep.slaveNodeId === nodeId ||
          dep.slaveNodeId === initiativeId
      );
    });
    return !hasVisibleOutgoing && !hasVisibleIncoming;
  }, [node, allInitiatives, isDependencyMode]);

  const getOpacity = () => {
    if (isOrphan) return 0.15;
    const isGreyDueToFilter = activeFilterIds.length > 0 && isNotInFilter;

    // Gantt isolation logic - only when isolation is active AND dependency mode is ON
    if (isDependencyMode && isIsolationActive) {
      // If this bar is isolated or connected to isolated, show normally
      if (isIsolated || isConnectedToIsolated) {
        return isGreyDueToFilter ? 0.15 : 1;
      }
      // Otherwise, dim it
      return 0.15;
    }

    return isGreyDueToFilter ? 0.15 : 1;
  };

  const progressBarColor =
    activeFilterIds.length && node.color ? node.color : theme.palette.brand.progressBar;

  const { position, width, hasSquareLeft, hasSquareRight, shouldHide } = useMemo(() => {
    const { startDate, endDate } = getInitiativeDates(node.initiatives);
    const totalDays = timelineEnd.diff(timelineStart, 'days');

    // Handle missing dates - bars appear "cut off" at edges
    let leftPercent = 0;
    let widthPercent = 0;
    let squareLeft = false;
    let squareRight = false;

    if (!startDate && !endDate) {
      // Both dates missing - hide the bar
      return {
        position: 0,
        width: 0,
        hasSquareLeft: false,
        hasSquareRight: false,
        shouldHide: true,
      };
    }

    if (!startDate && endDate) {
      // No start date - bar starts at timeline start (appears cut off on left)
      const endDateObj = dayjs(endDate);
      const daysToEnd = endDateObj.diff(timelineStart, 'days');
      leftPercent = 0; // Start at timeline beginning
      widthPercent = Math.min(100, (daysToEnd / totalDays) * 100); // Extend to end date
      squareLeft = true;
    }

    if (startDate && !endDate) {
      // No end date - bar extends to timeline end (appears cut off on right)
      const startDateObj = dayjs(startDate);
      const daysSinceStart = startDateObj.diff(timelineStart, 'days');
      leftPercent = Math.max(0, (daysSinceStart / totalDays) * 100);
      widthPercent = 100 - leftPercent; // Extend to timeline end
      squareRight = true;
    }

    if (startDate && endDate) {
      // Both dates present - normal behavior
      const startDateObj = dayjs(startDate);
      const endDateObj = dayjs(endDate);
      const daysSinceStart = startDateObj.diff(timelineStart, 'days');
      const durationDays = endDateObj.diff(startDateObj, 'days') + 1;

      leftPercent = (daysSinceStart / totalDays) * 100;
      widthPercent = (durationDays / totalDays) * 100;
    }

    return {
      position: Math.max(0, leftPercent),
      width: Math.max(1, widthPercent), // Minimum 1% width for visibility
      hasSquareLeft: squareLeft,
      hasSquareRight: squareRight,
      shouldHide: false,
    };
  }, [node.initiatives, timelineStart, timelineEnd]);

  const { startDate, endDate } = getInitiativeDates(node.initiatives);

  // Extract progress percentage from initiative data (matches Swimlane logic)
  const getProgressPercentage = (): number => {
    const progressValue =
      node.initiatives?.progressField ||
      node.initiatives?.fields?.find(f => f.type === InitiativeFieldTypes.progressBar)?.value ||
      '0';

    const numericValue = Number(progressValue);
    return Number.isNaN(numericValue) ? 0 : Math.min(Math.max(numericValue, 0), 100);
  };

  const progressPercentage = getProgressPercentage();

  const handleBarClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling

    // In dependency mode, handle isolation on bar click
    if (isDependencyMode && onBarClick) {
      // Use initiative ID for isolation (prefer initiatives.id if available, fallback to node.id)
      const isolationId = node.initiatives?.id || node.id;
      if (isolationId) {
        onBarClick(isolationId);
        return;
      }
    }

    // Default behavior: open initiative details dialog
    if (node.id) {
      setSelectedNodeId(node.id);
      setIsInitiativeDetailDialogOpen(true);
    }
  };

  // Format dates for tooltip
  const formatDateForTooltip = (date: string | null) => {
    if (!date) return 'No date set';
    return dayjs(date).format('MMM D, YYYY');
  };
  const tooltipText = `${formatDateForTooltip(startDate)} - ${formatDateForTooltip(endDate)}`;

  // Calculate vertical position - use provided position if available
  const barHeight = depth > 0 ? 10 : 16; // Smaller height for children

  // Calculate top position within the row
  const topPosition =
    providedTopPosition === undefined
      ? (() => {
          // Fallback to old calculation if no position provided
          const paddingAboveEachBar = 6;
          const paddingBelowEachBar = 6;
          const itemTotalHeight = 16 + paddingAboveEachBar + paddingBelowEachBar; // 28px
          const childOffsetAdjustment = depth > 0 ? 3 : 0;
          return paddingAboveEachBar + initiativeIndex * itemTotalHeight + childOffsetAdjustment;
        })()
      : providedTopPosition + (rowHeight - barHeight) / 2; // Center bar in row

  // Don't render bar if both dates are missing
  if (shouldHide) {
    return null;
  }

  return (
    <BarContainer
      left={position}
      width={width}
      top={topPosition}
      opacity={getOpacity()}
      onClick={handleBarClick}
      title={tooltipText}
      depth={depth}
      $isIsolated={isIsolated}
      $isConnectedToIsolated={isConnectedToIsolated}
      $isIsolationActive={isIsolationActive && isDependencyMode}
    >
      <ProgressBarContent
        progressPercentage={progressPercentage}
        barColor={progressBarColor}
        hasSquareLeft={hasSquareLeft}
        hasSquareRight={hasSquareRight}
      />
    </BarContainer>
  );
};

const BarContainer = styled.div<{
  left: number;
  opacity: number;
  top: number;
  width: number;
  $isConnectedToIsolated?: boolean;
  $isIsolated?: boolean;
  $isIsolationActive?: boolean;
  depth?: number;
}>`
  position: absolute;
  left: ${({ left }) => left}%;
  width: ${({ width }) => width}%;
  top: ${({ top }) => top}px;
  height: ${({ depth = 0 }) => (depth > 0 ? '10px' : '16px')};
  z-index: 1;
  cursor: pointer;
  opacity: ${({ opacity }) => opacity};
`;

const ProgressBarContent = styled.div<{
  barColor: string;
  hasSquareLeft: boolean;
  hasSquareRight: boolean;
  progressPercentage: number;
}>`
  height: 100%;
  border-radius: ${({ hasSquareLeft, hasSquareRight }) => {
    if (hasSquareLeft && hasSquareRight) return '0'; // Both square
    if (hasSquareLeft) return '0 1rem 1rem 0'; // Square left, round right
    if (hasSquareRight) return '1rem 0 0 1rem'; // Round left, square right
    return '1rem'; // Both round (normal)
  }};
  background: linear-gradient(
    to right,
    ${({ barColor }) => barColor} 0%,
    ${({ barColor }) => barColor} ${({ progressPercentage }) => progressPercentage}%,
    ${({ barColor }) => barColor}80 ${({ progressPercentage }) => progressPercentage}%,
    ${({ barColor }) => barColor}80 100%
  );
`;

export { InitiativeBar };
